import { ApiRequestError } from "@/lib/api/api-error";
import {
  type RegistrationCapture,
  saveGuestRegistration,
} from "@/lib/api/guest-registration";

/**
 * Durable retry queue for identity captures whose POST didn't land.
 *
 * This bundle never queues: its desk scans save through the server, online.
 * It drains what an older bundle queued (a guest row that already carries the
 * document and still owes a filing) until `MAX_AGE_MS` retires the entry.
 *
 * An entry stays only while a retry could plausibly send it: a lost connection
 * or a server-side error. A refusal (validation, permission, a reservation that
 * no longer accepts changes) would fail identically forever, so it surfaces to
 * the host instead of accumulating.
 *
 * An entry belongs to the staff member who took the scan, and only they can
 * send it. `applyHostRegistrationEdit` stamps `idVerifiedByStaffId` from the
 * SESSION that posts, so flushing someone else's capture would sign their
 * attestation with the wrong name — on a shared front-desk tablet across a
 * shift change, that silently falsifies who checked the document.
 */

const STORAGE_KEY = "superhost.registration.outbox";

/** Newest-wins cap on what a flush writes back. */
const MAX_ENTRIES = 50;

/**
 * How long a capture may wait for its own author to sign back in. Mirrors the
 * offline session cache (and better-auth's 7-day session), so an entry cannot
 * outlive the session that could have sent it — which also stops passport data
 * sitting in local storage indefinitely on a device nobody returns to.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Below this, the request never reached the server (see `apiRequest`). */
const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 500;

/**
 * 4xx codes that describe the *moment*, not the body. A queue that drains after
 * a long offline stretch hits these routinely: the stored session has lapsed
 * and has not been refreshed yet (401), the request timed out (408), or the
 * burst of a whole queue draining at once got throttled (429). Filing them as
 * final would discard a capture the very next attempt would have accepted.
 */
const RETRIABLE_CLIENT_ERRORS = new Set([401, 408, 429]);

export interface QueuedCapture extends RegistrationCapture {
  /** When the host took the scan, not when the retry runs. */
  capturedAt: string;
  /**
   * The staff member who reviewed the document. Only their session may send
   * this entry, because the server attests the registration to whoever posts
   * it. `null` on an entry queued before this field existed: unattributable, so
   * it is never sent rather than sent under the wrong name.
   */
  capturedByUserId: string | null;
  /**
   * Who the scan was of, for the message the host reads if it never files.
   * Stored rather than looked up: the queue is drained at app start or on
   * reconnect, when the guest row may not have synced to this device yet, and
   * "a guest registration failed" is not something anyone can act on.
   */
  guestName: string;
  /**
   * Identity of this queued attempt. A flush diffs the queue it snapshotted
   * against the queue as it stands afterwards; keying that on content
   * (guest + reservation + timestamp) cannot tell a corrected re-scan from the
   * scan it corrects when both land in the same millisecond, and silently
   * discards one of them. An id assigned at queue time always can.
   */
  id: string;
}

/**
 * Whether re-sending later could succeed. Most 4xx codes are the server's
 * considered refusal of this exact body and will be refused again; everything
 * else (no connection, 5xx, a proxy hiccup) is worth another attempt, as are
 * the few 4xx codes that are about timing rather than content.
 */
export function isRetriable(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) {
    return false;
  }
  if (RETRIABLE_CLIENT_ERRORS.has(error.status)) {
    return true;
  }
  return !(error.status >= CLIENT_ERROR_MIN && error.status < CLIENT_ERROR_MAX);
}

function readOutbox(): QueuedCapture[] {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedCapture[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(entries: QueuedCapture[]): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    // A full or unavailable store must not take the app down with it; the
    // flush still reports what it sent and dropped.
    console.error("[registration] could not persist the retry queue:", err);
  }
}

/**
 * One capture per guest per reservation: the newest review wins. A corrected
 * re-scan must replace the scan it corrects, or the superseded one is filed
 * too and the record carries data the host already rejected.
 *
 * Applied on write-back: a flush writes back a set it snapshotted before a
 * re-scan arrived from an older bundle's tab, which would otherwise resurrect
 * the entry the re-scan replaced.
 */
function keepNewestPerGuest(entries: QueuedCapture[]): QueuedCapture[] {
  const newest = new Map<string, QueuedCapture>();
  for (const entry of entries) {
    const key = `${entry.reservationId}:${entry.guestId}`;
    const held = newest.get(key);
    const isNewer =
      !held || Date.parse(entry.capturedAt) >= Date.parse(held.capturedAt);
    if (isNewer) {
      newest.set(key, entry);
    }
  }
  return [...newest.values()];
}

export function pendingRegistrationCount(): number {
  return readOutbox().length;
}

/** One entry's fate after a retry: keep it queued, or let it leave. */
async function retryCapture(entry: QueuedCapture): Promise<{
  dropped: boolean;
  keep: boolean;
  sent: boolean;
}> {
  try {
    await saveGuestRegistration(entry);
    return { dropped: false, keep: false, sent: true };
  } catch (err) {
    if (isRetriable(err)) {
      return { dropped: false, keep: true, sent: false };
    }
    // Refused for good — the stay stopped accepting registration changes, or
    // the data itself is rejected. Retrying cannot fix it, so the entry leaves
    // the queue, but the guest is still legally unregistered: report it as
    // dropped so the caller can put that in front of the host.
    console.error(
      "[registration] queued capture permanently refused:",
      entry.guestId,
      err
    );
    return { dropped: true, keep: false, sent: false };
  }
}

/**
 * What one flush did. The unfiled halves carry their entries rather than a
 * count, because the host cannot act on "a registration failed" — they need to
 * know which guest.
 */
export interface FlushResult {
  /**
   * Waiting for the colleague who took them to sign back in on this device.
   * Still owed, still queued — just not this session's to send.
   */
  deferred: QueuedCapture[];
  /** Gone and NOT registered: refused for good, or waited past `MAX_AGE_MS`. */
  dropped: QueuedCapture[];
  /** Filed successfully. */
  sent: number;
}

/** A fresh result each time: the arrays are the caller's to hold, not shared. */
const nothingFlushed = (): FlushResult => ({
  deferred: [],
  dropped: [],
  sent: 0,
});

/** Guards against two overlapping flushes filing the same capture twice. */
let inFlight = false;

/** True when this session is the one that reviewed the document. */
function isOwnedBy(entry: QueuedCapture, userId: string | null): boolean {
  return entry.capturedByUserId !== null && entry.capturedByUserId === userId;
}

/**
 * Re-send the captures THIS session took. Entries that succeed (or are refused
 * for good) leave the queue; entries that fail retriably stay for the next
 * flush; entries belonging to another staff member are left untouched, because
 * sending them would attest their scan to the wrong person.
 *
 * Captures are independent — one per guest — so they go out together rather
 * than making the last guest in a long queue wait out every round trip.
 */
export async function flushRegistrationOutbox(
  currentUserId: string | null
): Promise<FlushResult> {
  // The hook flushes on mount AND on every `online` event, and a reconnect
  // arriving mid-POST is the normal case rather than a rare race. Without this
  // both runs read the same queue before either writes back, and the same
  // capture is filed twice — two rows in an append-only legal record. The
  // second caller reports nothing; the one already running will.
  if (inFlight) {
    return nothingFlushed();
  }
  inFlight = true;
  try {
    return await drainOutbox(currentUserId);
  } finally {
    inFlight = false;
  }
}

async function drainOutbox(currentUserId: string | null): Promise<FlushResult> {
  const entries = readOutbox();
  if (entries.length === 0) {
    return nothingFlushed();
  }

  // An entry nobody can send any more is dropped rather than left to sit with
  // passport data in it; the host is told, same as a refusal.
  const cutoff = Date.now() - MAX_AGE_MS;
  const expired = entries.filter(
    (entry) => Date.parse(entry.capturedAt) < cutoff
  );
  for (const entry of expired) {
    console.error(
      "[registration] queued capture expired unfiled:",
      entry.guestId,
      entry.capturedAt
    );
  }
  const live = entries.filter((entry) => !expired.includes(entry));

  const mine = live.filter((entry) => isOwnedBy(entry, currentUserId));
  const deferred = live.filter((entry) => !isOwnedBy(entry, currentUserId));

  const results = await Promise.all(mine.map(retryCapture));
  const stillPending = mine.filter((_, i) => results[i].keep);
  const sent = results.filter((r) => r.sent).length;
  const refused = mine.filter((_, i) => results[i].dropped);

  // Re-read before writing: a scan an older bundle's tab queued during the
  // flush would otherwise be clobbered by this snapshot of the queue.
  const seen = new Set(entries.map((entry) => entry.id));
  const queuedDuringFlush = readOutbox().filter((entry) => !seen.has(entry.id));
  writeOutbox(
    keepNewestPerGuest([
      ...deferred,
      ...stillPending,
      ...queuedDuringFlush,
    ]).slice(-MAX_ENTRIES)
  );
  return { deferred, dropped: [...refused, ...expired], sent };
}
